import { Controller, useFormContext } from 'react-hook-form';
import {
  Grid,
  Radio,
  FormControlLabel,
  FormControl,
  FormLabel,
  InputLabel,
  MenuItem,
  RadioGroup,
  Select,
  useTheme,
  InputAdornment,
  FormHelperText,
} from '@mui/material';
import type { CountryEntity } from '@shared/api';
import { Input } from '@shared';

const DELIVERED_RADIOS = [
  { label: 'Home Address', value: 'Home Address' },
  { label: 'Alternative Mailing Address', value: 'Alternative Address' },
];
const BORDIER_OFFICE_RADIOS = [
  { label: 'Zurich', value: 'Zurich' },
  { label: 'Geneva', value: 'Geneva' },
  { label: 'Nyon', value: 'Nyon' },
  { label: 'Bern', value: 'Bern' },
];

interface Props {
  countries: CountryEntity[];
}

export const StepContact = ({ countries }: Props) => {
  const theme = useTheme();

  const {
    control,
    formState: { errors },
    getValues,
    setValue,
    watch,
  } = useFormContext();
  const deliveryAddress = watch('deliveryAddress');

  const useAlternativeAddress = deliveryAddress === 'Alternative Address';

  return (
    <Grid container>
      <Grid item xs={6}>
        <Controller
          name='residentialAddressLine1'
          control={control}
          rules={{
            required: 'Residential Address is required',
            maxLength: { value: 40, message: 'Maximum length for Residential Address is 40 characters' },
          }}
          render={({ field, fieldState: { error } }) => (
            <Input {...field} label='Residential Address' margin='normal' required error={!!error} helperText={error?.message} />
          )}
        />
      </Grid>
      <Grid item xs={6}>
        <Controller
          name='residentialAddressLine2'
          control={control}
          rules={{
            required: 'Address Line 2 is required',
            maxLength: { value: 40, message: 'Maximum length for Address Line 2 is 40 characters' },
          }}
          render={({ field, fieldState: { error } }) => (
            <Input {...field} label='Address Line 2' margin='normal' required error={!!error} helperText={error?.message} />
          )}
        />
      </Grid>
      <Grid item xs={4}>
        <Controller
          name='city'
          control={control}
          rules={{ required: 'City is required' }}
          render={({ field, fieldState: { error } }) => (
            <Input {...field} label='City' margin='normal' required error={!!error} helperText={error?.message} />
          )}
        />
      </Grid>
      <Grid item xs={4}>
        <Controller
          name='postcode'
          control={control}
          rules={{ required: 'Postcode is required', maxLength: { value: 10, message: 'Maximum length for Postcode is 10 characters' } }}
          render={({ field, fieldState: { error } }) => (
            <Input {...field} label='Postcode' margin='normal' required error={!!error} helperText={error?.message} />
          )}
        />
      </Grid>
      <Grid item xs={4}>
        {countries.length > 0 ? (
          <FormControl margin='normal' fullWidth error={!!errors.country}>
            <InputLabel>Country *</InputLabel>
            <Controller
              name='country'
              control={control}
              rules={{ required: 'Country is required' }}
              render={({ field, fieldState: { error } }) => (
                <>
                  <Select {...field} variant='standard' fullWidth>
                    {countries.map((item, idx) => (
                      <MenuItem key={idx} value={item.name}>
                        {item.name}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>{error?.message}</FormHelperText>
                </>
              )}
            />
          </FormControl>
        ) : (
          <Controller
            name='country'
            control={control}
            rules={{ required: 'Country is required' }}
            render={({ field, fieldState: { error } }) => (
              <Input {...field} label='Country' margin='normal' required error={!!error} helperText={error?.message} />
            )}
          />
        )}
      </Grid>
      <Grid item xs={6}>
        <Controller
          name='alternativeMailingAddressLine1'
          control={control}
          rules={{
            required: useAlternativeAddress ? 'Alternative Mailing Address is required' : false,
            maxLength: { value: 40, message: 'Maximum length for Alternative Mailing Address is 40 characters' },
          }}
          render={({ field, fieldState: { error } }) => (
            <Input
              {...field}
              label='Alternative Mailing Address'
              margin='normal'
              required={useAlternativeAddress}
              error={!!error}
              helperText={error?.message}
            />
          )}
        />
      </Grid>
      <Grid item xs={6}>
        <Controller
          name='alternativeMailingAddressLine2'
          control={control}
          rules={{
            required: useAlternativeAddress ? 'Alternative Mailing Address Line 2 is required' : false,
            maxLength: { value: 40, message: 'Maximum length for Alternative Mailing Address Line 2 is 40 characters' },
          }}
          render={({ field, fieldState: { error } }) => (
            <Input
              {...field}
              label='Alternative Mailing Address Line 2'
              margin='normal'
              required={useAlternativeAddress}
              error={!!error}
              helperText={error?.message}
            />
          )}
        />
      </Grid>
      <Grid item xs={4}>
        <Controller
          name='alternativeMailingAddressCity'
          control={control}
          rules={{ required: useAlternativeAddress ? 'City is required' : false }}
          render={({ field, fieldState: { error } }) => (
            <Input {...field} label='City' margin='normal' required={useAlternativeAddress} error={!!error} helperText={error?.message} />
          )}
        />
      </Grid>
      <Grid item xs={4}>
        <Controller
          name='alternativeMailingAddressPostcode'
          control={control}
          rules={{
            required: useAlternativeAddress ? 'Postcode is required' : false,
            maxLength: { value: 10, message: 'Maximum length for Postcode is 10 characters' },
          }}
          render={({ field, fieldState: { error } }) => (
            <Input {...field} label='Postcode' margin='normal' required={useAlternativeAddress} error={!!error} helperText={error?.message} />
          )}
        />
      </Grid>
      <Grid item xs={4}>
        {countries.length > 0 ? (
          <FormControl fullWidth margin='normal' error={!!errors.alternativeMailingAddressCountry}>
            <InputLabel>Country{useAlternativeAddress && ' *'}</InputLabel>
            <Controller
              name='alternativeMailingAddressCountry'
              control={control}
              rules={{ required: useAlternativeAddress ? 'Country is required' : false }}
              render={({ field, fieldState: { error } }) => (
                <>
                  <Select {...field} variant='standard' fullWidth>
                    {countries.map((item, idx) => (
                      <MenuItem key={idx} value={item.name}>
                        {item.name}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>{error?.message}</FormHelperText>
                </>
              )}
            />
          </FormControl>
        ) : (
          <Controller
            name='alternativeMailingAddressCountry'
            control={control}
            rules={{ required: useAlternativeAddress ? 'Country is required' : false }}
            render={({ field, fieldState: { error } }) => (
              <Input {...field} label='Country' margin='normal' required={useAlternativeAddress} error={!!error} helperText={error?.message} />
            )}
          />
        )}
      </Grid>
      <Grid item xs={4}>
        <Controller
          name='mobileNumber'
          control={control}
          rules={{
            required: 'Mobile Telephone Number is required',
            maxLength: { value: 20, message: 'Maximum length for Mobile Telephone Number is 20 characters' },
          }}
          render={({ field, fieldState: { error } }) => (
            <Input
              {...field}
              label='Mobile Telephone Number'
              margin='normal'
              required
              error={!!error || !!errors.mobileAreaCode}
              helperText={(errors.mobileAreaCode?.message as string) || error?.message}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start' sx={{ width: '80px' }}>
                    {countries.length > 0 ? (
                      <Controller
                        name='mobileAreaCode'
                        control={control}
                        rules={{ required: 'Mobile Telephone Number is required' }}
                        render={({ field }) => (
                          <Select {...field} variant='standard' fullWidth renderValue={(item) => `+ ${item}`}>
                            {countries.map((item, idx) => (
                              <MenuItem key={idx} value={item.code}>
                                +{item.code} {item.name}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      />
                    ) : (
                      <Controller
                        name='mobileAreaCode'
                        control={control}
                        rules={{ required: 'Mobile Telephone Number is required' }}
                        render={({ field }) => (
                          <Input
                            {...field}
                            inputProps={{ maxLength: 3 }}
                            InputProps={{ startAdornment: <InputAdornment position='start'>+</InputAdornment> }}
                          />
                        )}
                      />
                    )}
                  </InputAdornment>
                ),
              }}
            />
          )}
        />
      </Grid>
      <Grid item xs={4}>
        <Controller
          name='alternativeContactNumber'
          control={control}
          rules={{ maxLength: { value: 20, message: 'Maximum length for Mobile Telephone Number is 20 characters' } }}
          render={({ field, fieldState: { error } }) => (
            <Input
              {...field}
              label='Alternative Telephone Number'
              margin='normal'
              error={!!error || !!errors.alternateAreaCode}
              helperText={(errors.alternateAreaCode?.message as string) || error?.message}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start' sx={{ width: '80px' }}>
                    {countries.length > 0 ? (
                      <Controller
                        name='alternateAreaCode'
                        control={control}
                        render={({ field }) => (
                          <Select {...field} variant='standard' fullWidth renderValue={(item) => `+ ${item}`}>
                            {countries.map((item, idx) => (
                              <MenuItem key={idx} value={item.code}>
                                +{item.code} {item.name}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      />
                    ) : (
                      <Controller
                        name='alternateAreaCode'
                        control={control}
                        render={({ field }) => (
                          <Input
                            {...field}
                            inputProps={{ maxLength: 3 }}
                            InputProps={{ startAdornment: <InputAdornment position='start'>+</InputAdornment> }}
                          />
                        )}
                      />
                    )}
                  </InputAdornment>
                ),
              }}
            />
          )}
        />
      </Grid>
      <Grid item xs={4}>
        <Controller
          name='email'
          control={control}
          rules={{ required: 'Email is required' }}
          render={({ field, fieldState: { error } }) => (
            <Input {...field} label='Email' margin='normal' required error={!!error} helperText={error?.message} />
          )}
        />
      </Grid>
      <Grid item xs={12}>
        <FormControl component='fieldset' error={!!errors.deliveryAddress || !!errors.deliveryBordierOffice}>
          <FormLabel component='legend'>Where would you like your card to be delivered? *</FormLabel>
          <RadioGroup aria-label='position' row>
            {DELIVERED_RADIOS.map((radio, idx) => (
              <FormControlLabel
                key={idx}
                label={radio.label}
                labelPlacement='end'
                control={
                  <Controller
                    name='deliveryAddress'
                    control={control}
                    rules={{ required: !getValues('deliveryBordierOffice') }}
                    render={({ field }) => (
                      <Radio
                        color='primary'
                        {...field}
                        value={radio.value}
                        checked={radio.value === field.value}
                        onChange={(e) => {
                          field.onChange(e);
                          setValue('deliveryBordierOffice', '');
                        }}
                      />
                    )}
                  />
                }
              />
            ))}
            <FormControlLabel label='Bordier Office:' control={<></>} sx={{ paddingLeft: theme.spacing(4) }} />
            {BORDIER_OFFICE_RADIOS.map((radio, idx) => (
              <FormControlLabel
                key={idx}
                label={radio.label}
                labelPlacement='end'
                control={
                  <Controller
                    name='deliveryBordierOffice'
                    control={control}
                    rules={{ required: !getValues('deliveryAddress') }}
                    render={({ field }) => (
                      <Radio
                        color='primary'
                        {...field}
                        value={radio.value}
                        checked={radio.value === field.value}
                        onChange={(e) => {
                          field.onChange(e);
                          setValue('deliveryAddress', '');
                        }}
                      />
                    )}
                  />
                }
              />
            ))}
          </RadioGroup>
          {(errors.deliveryAddress || errors.deliveryBordierOffice) && <FormHelperText>Delivery Address is required</FormHelperText>}
        </FormControl>
      </Grid>
    </Grid>
  );
};
